import type { UIMessage } from "@ai-sdk/react";
import { getToolCallFriendlyName } from "@/features/companion";

export function extractMessageText(msg: UIMessage): string {
  return msg.parts
    .filter((p): p is { type: "text"; text: string } => p.type === "text")
    .map((p) => p.text)
    .join("");
}

export function isHiddenControlMessage(text: string): boolean {
  return (
    text === "__copilot_start__" ||
    text === "__jobs_completed__" ||
    text.startsWith("[MARCUS_ONBOARDING_V1_") ||
    text.startsWith("__step_briefing:") ||
    text.startsWith("__store_connected__") ||
    text.startsWith("__ecom_missing_data__") ||
    text.startsWith("__ecom_pipeline_error__") ||
    text.startsWith("__higgsfield_dispatched__:")
  );
}

/**
 * Strip injected context blocks (ads workspace, widget context, content studio)
 * from persisted user messages. These blocks are appended server-side for the LLM
 * but should never appear in chat history visible to the user.
 */
const INJECTED_CONTEXT_MARKERS = [
  "\n\n## Ads Workspace State",
  "\n\n## Attached Widget Context (User-Selected)",
  "\n\n## Content Studio Focus State",
];

export function stripInjectedContext(text: string): string {
  let result = text;
  for (const marker of INJECTED_CONTEXT_MARKERS) {
    const idx = result.indexOf(marker);
    if (idx !== -1) {
      result = result.slice(0, idx);
    }
  }
  return result;
}

export function formatSessionTimestamp(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

export function cleanSnippet(text: string, maxLen = 90): string {
  const normalized = text.replace(/\s+/g, " ").trim();
  if (!normalized) return "";
  if (normalized.length <= maxLen) return normalized;
  return `${normalized.slice(0, maxLen - 1)}...`;
}

export function listPreview(items: string[], limit = 3): string {
  const cleaned = items.map((item) => item.trim()).filter(Boolean);
  if (cleaned.length === 0) return "";
  const shown = cleaned.slice(0, limit);
  return `${shown.join(", ")}${cleaned.length > shown.length ? ", ..." : ""}`;
}

function summarizeToolValue(value: unknown): string {
  if (value == null) return "null";
  if (typeof value === "string")
    return value.length > 48 ? `"${value.slice(0, 45)}..."` : `"${value}"`;
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  if (Array.isArray(value)) return `[${value.length}]`;
  if (typeof value === "object")
    return `{${Object.keys(value as Record<string, unknown>).length} keys}`;
  return typeof value;
}

export function isStrategyConfirmIntent(text: string): boolean {
  const normalized = text.trim().toLowerCase();
  return /^(yes|yep|yeah|ok|okay|confirm|approved|approve|continue|let'?s go|go ahead|proceed)\b/.test(
    normalized,
  );
}

export function summarizeToolInput(input: unknown): string {
  if (!input || typeof input !== "object") return "";
  const obj = input as Record<string, unknown>;
  const keys = Object.keys(obj);
  if (keys.length === 0) return "no args";
  const parts = keys.slice(0, 4).map((key) => `${key}=${summarizeToolValue(obj[key])}`);
  return `${parts.join(", ")}${keys.length > 4 ? ", ..." : ""}`;
}

// ── Human-readable tool call descriptions ────────────────────────────────────

type ToolCopyFn = (input: Record<string, unknown>) => string;

const TOOL_COPY: Record<string, string | ToolCopyFn> = {
  set_step: (input) => {
    const step = input.stepIndex;
    return step != null
      ? `${getToolCallFriendlyName("set_step")} (Step ${Number(step) + 1})`
      : getToolCallFriendlyName("set_step");
  },
  run_coagent_step: (input) => {
    const step = input.step;
    return step != null
      ? `${getToolCallFriendlyName("run_coagent_step")} (Step ${Number(step) + 1})`
      : getToolCallFriendlyName("run_coagent_step");
  },
  update_brand_field: (input) => {
    const path = typeof input.path === "string" ? input.path.split(".").pop() : "";
    return path
      ? `Updating brand ${path
          .replace(/([A-Z])/g, " $1")
          .toLowerCase()
          .trim()}`
      : getToolCallFriendlyName("update_brand_field");
  },
  ads_navigate: (input) => {
    if (typeof input.tab === "string" && input.tab) {
      return `${getToolCallFriendlyName("ads_navigate")} (${input.tab})`;
    }
    if (typeof input.productName === "string" && input.productName) {
      return `Selecting ${input.productName}`;
    }
    if (typeof input.adId === "string" && input.adId) {
      return "Opening ad detail";
    }
    return getToolCallFriendlyName("ads_navigate");
  },
};

/**
 * Convert a tool name + input into a human-readable description.
 * Returns `null` if no copy is defined for this tool — caller should fall back.
 */
export function humanizeToolCall(toolName: string, input: unknown): string | null {
  const copy = TOOL_COPY[toolName];
  if (!copy) return getToolCallFriendlyName(toolName);
  if (typeof copy === "string") return copy;
  const obj = (input && typeof input === "object" ? input : {}) as Record<string, unknown>;
  return copy(obj);
}
